import React from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Divider, Typography } from '@mui/material';

import {
  CasemasterAssessmentStates,
  QuestionStates,
} from './constant';
import useCaseMasterStore from './CaseMasterStore';
import CustomButton from 'components/CustomButton';
import MessageIndicator from 'components/MessageIndicator';
import WavRecorder from 'utils/WavRecorder';
import ActionTimerDialog from 'dialogs/ActionTimerDialog';
import { Session } from 'utils';
import { textToSpeech } from 'utils/speech';
import MicTestAnimation from 'components/MicTestAnimation';
import { analyseCaseMasterProblem } from 'services';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  conversationArea: {
    flex: 1,
    padding: theme.spacing(6),
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
  },
  messageContainer: {
    display: 'flex',
    width: '100%',
    marginBottom: theme.spacing(2.5),
  },
  interviewerMessage: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    width: '100%',
  },
  userMessage: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    width: '100%',
  },
  messageBubble: {
    maxWidth: '46%',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    wordWrap: 'break-word',
  },
  interviewerBubble: {
    border: '1px solid #E5E7EB',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  userBubble: {
    backgroundColor: '#003E8C',
    color: '#FFFFFF',
  },
  analysingBubble: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
  },
  recordMessageBubble: {
    maxWidth: '46%',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    border: '1px solid #E5E7EB',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  recordMessageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  autoSaveText: {
    color: '#6B7280',
    fontSize: '14px',
    fontFamily: 'Inter, sans-serif',
  },
  caseStudyHeader: {
    marginBottom: theme.spacing(4)
  },
  card: {
    width: '75%',
    borderRadius: theme.spacing(2),
    border: '1px solid',
    borderColor: theme.palette.neutral['clr-200'],
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  cardContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  interviewerCardContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
  },
  divider: {
    height: '0.5px',
    width: '100%',
    backgroundColor: theme.palette.neutral['clr-200']
  },
  errorState: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.danger['clr-100'],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1, 2),
  },
  IntervieweeCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  caseContent: {
    lineHeight: '152%',
    '& h3': {
      color: theme.palette.primary['clr-600'],
      marginBottom: theme.spacing(2)
    },
    '& h4': {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1.5)
    },
    '& p': {
      marginBottom: theme.spacing(1.5)
    },
    '& strong': {
      fontWeight: 600
    }
  }
}));

const AnimatedEllipsis = ({ text = "Saving answer", interval = 500, maxDots = 3 }) => {
  const [dots, setDots] = React.useState(1);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setDots(prev => (prev % maxDots) + 1);
    }, interval);
    return () => clearInterval(timer);
  }, [interval, maxDots]);

  const ellipsis = '.'.repeat(dots) + '\u00A0'.repeat(maxDots - dots);

  return (
    <span
      style={{
        display: 'inline-block',
        minWidth: `${text.length + maxDots - 2}ch`,
        fontFamily: 'inherit',
        textAlign: 'left',
        verticalAlign: 'middle',
      }}
    >
      {text}
      <span style={{ fontFamily: 'monospace' }}>{ellipsis}</span>
    </span>
  );
};

const QuestionSection = () => {
  const classes = useStyles();
  const containerRef = React.useRef(null);
  const wavRecorder = React.useRef(null);
  const recordStartTimeRef = React.useRef(0);
  const timeLeftRef = React.useRef(0);
  const audioRef = React.useRef(null);
  const followupCountRef = React.useRef(0);

  const [casemasterProblem, questionType, setInterviewerState] = useCaseMasterStore(state => [
    state.casemasterProblem, state.questionType, state.setInterviewerState
  ]);

  const [
    questionState, setQuestionState, timeLeft,
    assessmentState, updateAnalysis, analysis, setAssessmentState, attempt, setHasGivenResponse
  ] = useCaseMasterStore(state => [
    state.questionState, state.setQuestionState, state.timeLeft,
    state.assessmentState, state.updateAnalysis, state.analysis, state.setAssessmentState, state.attempt, state.setHasGivenResponse
  ]);

  const [conversations, setConversations] = React.useState([]);
  const [analysing, setAnalysing] = React.useState(false);
  const [active, setActive] = React.useState(false);
  const [openFinalSubmittionDialog, setOpenFinalSubmittionDialog] = React.useState(false);
  const [isAssessmentComplete, setIsAssessmentComplete] = React.useState(false);
  const [isInterviewerSpeaking, setIsInterviewerSpeaking] = React.useState(false);
  const [chunk, setChunk] = React.useState('');
  const [currentTypingMessageIndex, setCurrentTypingMessageIndex] = React.useState(-1);
  const typeIntervalRef = React.useRef(null);
  const [recordingTimeLeft, setRecordingTimeLeft] = React.useState(120); // 2 minutes in seconds
  const recordingTimerRef = React.useRef(null);
  const [showingFinalTranscript, setShowingFinalTranscript] = React.useState(false);
  const finalTranscriptShownRef = React.useRef(false);
  const [isError, setIsError] = React.useState(false);

  const clearTypeInterval = React.useCallback(() => {
    if (typeIntervalRef.current) {
      clearInterval(typeIntervalRef.current);
      typeIntervalRef.current = null;
    }
  }, []);

  const typeText = React.useCallback((textToType = '', audioDuration = 0) => {
    clearTypeInterval();
    // Reset chunk to start typing from beginning
    setChunk('');
    let charIndex = 0;

    // Calculate typing speed to match audio duration
    // If audio duration is available, sync typing speed with it
    // Otherwise, use default 45ms per character
    const typingInterval = audioDuration > 0
      ? (audioDuration * 1000) / textToType.length
      : 45;

    typeIntervalRef.current = setInterval(() => {
      setIsInterviewerSpeaking(true);
      if (charIndex < textToType.length) {
        setChunk((prev) => prev + textToType.charAt(charIndex));
        charIndex++;
      } else {
        setIsInterviewerSpeaking(false);
        setCurrentTypingMessageIndex(-1);
        clearTypeInterval();
      }
    }, typingInterval);
  }, [clearTypeInterval]);

  // Speak helper using common TTS utility
  const speak = React.useCallback(async (text = '') => {
    if (!text) return;
    try {
      setInterviewerState('speaking');

      // Wait for audio to be ready and start typing when it starts playing
      const audio = audioRef.current;
      if (audio) {
        // Add event listener for when audio starts playing
        const onPlaying = () => {
          // Get audio duration and start typewriter with synced speed
          const audioDuration = audio.duration;
          typeText(text, audioDuration);
          audio.removeEventListener('playing', onPlaying);
        };
        audio.addEventListener('playing', onPlaying);

        // Also handle error case - if audio fails to load, start typewriter anyway
        const onError = () => {
          typeText(text, 0); // Use default speed
          audio.removeEventListener('error', onError);
          audio.removeEventListener('playing', onPlaying);
        };
        audio.addEventListener('error', onError, { once: true });
      }

      await textToSpeech({
        text,
        language: 'en-GB',
        voiceId: 'Amy',
        audioRef
      });
    } catch (error) {
      console.error('TTS error:', error);
      // On error, fallback to end of speaking state
      setInterviewerState('listening');
      setQuestionState(QuestionStates.WAITING);
    }
  }, [setInterviewerState, setQuestionState, typeText]);

  // Initialize conversation with problem statement
  React.useEffect(() => {
    if (casemasterProblem?.problem && conversations.length === 0) {
      setConversations([{
        type: 'interviewer',
        message: casemasterProblem.problem,
        timestamp: new Date()
      }]);
      setQuestionState(QuestionStates.AI_SPEAKING);
    }
  }, [casemasterProblem, conversations.length]);

  React.useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  const startRecording = () => {
    // Do not allow recording while AI is speaking
    if (questionState === QuestionStates.AI_SPEAKING || isInterviewerSpeaking) return;
    if (assessmentState !== CasemasterAssessmentStates.RUNNING) {
      setAssessmentState(CasemasterAssessmentStates.RUNNING);
    }
    
    wavRecorder.current = new WavRecorder();
    wavRecorder.current.start();
    recordStartTimeRef.current = timeLeft;
    setIsError(false);
    setActive(true);
    setQuestionState(QuestionStates.RECORDING);
    
    // Start 2-minute countdown timer
    setRecordingTimeLeft(120);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    recordingTimerRef.current = setInterval(() => {
      setRecordingTimeLeft((prev) => {
        if (prev <= 1) {
          // Timer reached 0, auto-save
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
          // Trigger end recording
          setTimeout(() => endRecording(), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRecordedAudio = React.useCallback((audioBlob) => {
    setAnalysing(true);
    setQuestionState(QuestionStates.ANALYZING);

    const duration = recordStartTimeRef.current - timeLeftRef.current;

    const handleEvent = (event, data) => {
      if (event === 'error') {
        setAnalysing(false);
        setIsError(true);
        setQuestionState(QuestionStates.WAITING);
      } else if (event === 'progress') {
        // Upload in progress
      } else if (event === 'analysed') {
        updateAnalysis({ explanation: data?.transcript, ...data });
        setAnalysing(false);

        if (data?.analysis?.isError) {
          setIsError(true);
          setQuestionState(QuestionStates.WAITING);
          return;
        }

        // Mark that user has given at least one response
        setHasGivenResponse(true);

        // Add user's answer (transcript) to conversation
        if (data?.transcript) {
          setConversations(prev => [...prev, {
            type: 'user',
            message: data.transcript,
            timestamp: new Date()
          }]);
        }

        // Check if interview is complete based on API response
        const shouldFinish = data?.followup?.conclusion === "True";

        if (shouldFinish) {
          // Assessment complete - mark to show final transcript before redirecting
          setShowingFinalTranscript(true);
        } else if (data?.followup?.message) {
          // More follow-up questions available
          followupCountRef.current += 1;
          // Add follow-up question
          setConversations(prev => [...prev, {
            type: 'interviewer',
            message: data.followup.message,
            timestamp: new Date()
          }]);
          setQuestionState(QuestionStates.AI_SPEAKING);
        } else {
          // conclusion is "False" or message is missing — stay in waiting state
          setQuestionState(QuestionStates.WAITING);
        }
      }
    };

    analyseCaseMasterProblem({
      audioBlob,
      duration,
      casemasterAttemptId: attempt?._id,
    }, handleEvent);
  }, [attempt, updateAnalysis, setQuestionState]);

  const endRecording = () => {
    setQuestionState(QuestionStates.RECORDED);
    setActive(false);
    
    // Clear recording timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    
    if (wavRecorder.current) {
      wavRecorder.current.stop((audioBlob) => {
        handleRecordedAudio(audioBlob);
      });
      wavRecorder.current = null;
    }
  };

  // Auto-scroll to bottom when new messages are added
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      container.scrollTop = container.scrollHeight;
    });

    observer.observe(container, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, [conversations]);

  // Attach audio end listener to transition states
  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => {
      setInterviewerState('listening');
      setIsInterviewerSpeaking(false);
      clearTypeInterval();
      setQuestionState(QuestionStates.WAITING);
    };
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('ended', onEnded);
      // Clean up any pending typewriter animation
      clearTypeInterval();
    };
  }, [setInterviewerState, setQuestionState, clearTypeInterval]);

  // When AI should speak, play the latest interviewer message
  React.useEffect(() => {
    if (questionState !== QuestionStates.AI_SPEAKING) return;

    // Find the last interviewer message and its index
    const lastInterviewerIndex = conversations.map((c, i) => ({ ...c, index: i }))
      .reverse()
      .find(c => c.type === 'interviewer')?.index;

    const lastInterviewerMsg = lastInterviewerIndex !== undefined ? conversations[lastInterviewerIndex] : null;

    if (lastInterviewerMsg?.message) {
      // Set the typing message index immediately to show empty chunk (prevents full text flash)
      setCurrentTypingMessageIndex(lastInterviewerIndex);
      setChunk('');
      speak(lastInterviewerMsg.message);
    } else {
      // No message found; recover to waiting
      setQuestionState(QuestionStates.WAITING);
    }
  }, [questionState, conversations, speak, setQuestionState]);

  // Auto-scroll to bottom when conversations change
  React.useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [conversations]);

  // Cleanup recording timer and typewriter interval on unmount
  React.useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      clearTypeInterval();
    };
  }, [clearTypeInterval]);

  // Handle final transcript display before redirecting to analysis
  React.useEffect(() => {
    if (showingFinalTranscript && !finalTranscriptShownRef.current) {
      finalTranscriptShownRef.current = true;

      // Use requestAnimationFrame to ensure DOM has been updated with the new transcript
      requestAnimationFrame(() => {
        // Scroll to bottom to ensure final transcript is visible
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }

        // Give user 1 second to see their final response before redirecting
        setTimeout(() => {
          setAssessmentState(CasemasterAssessmentStates.FINISHING);
        }, 1000);
      });
    }
  }, [showingFinalTranscript, setAssessmentState]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box className={classes.root}>
      <audio ref={audioRef} preload='auto'></audio>
      {/* Conversation Area */}
      <Box className={classes.conversationArea} ref={containerRef}>
        {/* Render dynamic conversations */}
        {conversations.map((conversation, index) => (
          <Box key={index} className={classes.messageContainer}>
            {conversation.type === 'interviewer' ? (
              <Box className={classes.interviewerMessage}>
                <Box className={`${classes.messageBubble} ${classes.interviewerBubble}`}>
                  <Typography variant="h6-semiBold" color="primary.main">
                    Interviewer
                  </Typography>

                  <Divider />

                  <Typography variant="body01-medium" color='neutral.clr-700'>
                    {
                      // Show typewriter chunk if this is the message currently being typed
                      index === currentTypingMessageIndex
                        ? chunk
                        : conversation.message
                    }
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box className={classes.userMessage}>
                <Box className={`${classes.messageBubble} ${classes.userBubble}`}>
                  <Typography variant="body01-medium" color="text.white">
                    {conversation.message}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        ))}

        {/* Show analysing indicator */}
        {analysing && (
          <Box className={classes.messageContainer}>
            <Box className={classes.userMessage}>
              <Box className={`${classes.messageBubble} ${classes.analysingBubble}`}>
                <MessageIndicator />
              </Box>
            </Box>
          </Box>
        )}

        {/* Show interviewer-side loading state when conclusion is reached */}
        {showingFinalTranscript && (
          <Box className={classes.messageContainer}>
            <Box className={classes.interviewerMessage}>
              <Box className={`${classes.messageBubble} ${classes.interviewerBubble}`}>
                <MessageIndicator />
              </Box>
            </Box>
          </Box>
        )}

        {/* Record Answer Component - Inline in conversation */}
        {!isAssessmentComplete && !analysing && !showingFinalTranscript && questionState !== QuestionStates.AI_SPEAKING && !isInterviewerSpeaking && (
          <Box className={classes.messageContainer}>
            <Box id="answer-section-container" className={classes.userMessage}>
              <Box id="answer-section" className={classes.recordMessageBubble}>
                <Box className={classes.recordMessageHeader}>
                  <Typography variant="h6-semiBold" color="primary.main">
                    You
                  </Typography>
                  {questionState === QuestionStates.RECORDING && active && (
                    <>
                      <Divider orientation="vertical" flexItem />
                      <Typography variant="h6-semiBold" color="primary.main">
                        {formatTime(recordingTimeLeft)}
                      </Typography>
                      <Divider orientation="vertical" flexItem />
                      <MicTestAnimation start={true} />
                    </>
                  )}
                </Box>
                
                <Divider />
                
                <Typography variant="body02-medium" className={classes.autoSaveText}>
                  Your answer will be auto-saved 2 minutes after recording begins.
                </Typography>

                <CustomButton
                  disabled={
                    analysing ||
                    questionState === QuestionStates.ANALYZING ||
                    questionState === QuestionStates.AI_SPEAKING ||
                    isInterviewerSpeaking
                  }
                  sx={{ width: '180px' }}
                  size="medium"
                  variant={questionState === QuestionStates.RECORDING ? 'outlined' : 'contained'}
                  onClick={
                    questionState === QuestionStates.RECORDING
                      ? endRecording : startRecording
                  }
                >
                  {analysing || questionState === QuestionStates.ANALYZING
                    ? <AnimatedEllipsis text="Analysing answer" />
                    : questionState === QuestionStates.RECORDING
                      ? "Save answer"
                      : isError ? "Answer again" : "Record answer"
                  }
                </CustomButton>
                {isError && (
                  <Box className={classes.errorState}>
                    <Typography variant='body01-bold' color='danger.clr-700'>
                      Oops! Interviewer couldn't hear you. Could you please try again?
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Final Submission Dialog */}
      {openFinalSubmittionDialog && (
        <ActionTimerDialog
          open={openFinalSubmittionDialog}
          onClose={() => setAssessmentState(CasemasterAssessmentStates.FINISHING)}
          title={`It was great talking to you, ${Session.user.name}`}
          timerMessage='Auto-submitting {timeLeft} seconds'
        />
      )}
    </Box>
  );
};

export default QuestionSection;